import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_URL, APP_URL } from '../config';

const RESERVATION_OPTIONS = ['bereits an ELITEHOMES AG bezahlt', 'direkt dem Eigentümer bezahlt', 'sonstige'];
const ZAHLUNG_OPTIONS = ['Handänderung', 'sonstige Vereinbarung'];
const HYPOTHEK_OPTIONS = ['nein', 'ja'];
const ZAHLUNGSVERSPRECHEN_OPTIONS = ['ja', 'nein'];
const BAULAND_OPTIONS = ['nein', 'ja'];
const BAULAND_DETAIL_OPTIONS = ['unerschlossen', 'vollerschlossen'];
const KOSTEN_OPTIONS = ['wird je zur Hälfte auf die Parteien aufgeteilt', 'Käuferschaft', 'Verkäuferschaft'];
const ZUSTELLUNG_OPTIONS = ['Vorerst nur an Makler', 'Alle Vertragsparteien und Makler', 'Vorerst nur an Verkäuferschaft', 'Vorerst nur an Käuferschaft', 'Verkäuferschaft und Käuferschaft'];

function formatCHF(value) {
  const num = String(value).replace(/[^0-9.]/g, '');
  const parts = num.split('.');
  let intPart = parts[0] || '';
  const decPart = parts[1] !== undefined ? '.' + parts[1].substring(0, 2) : '';
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return intPart ? "CHF " + intPart + decPart : '';
}

function parseCHF(formatted) { return formatted.replace(/[^0-9.]/g, ''); }
function isValidEmail(email) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email); }

function FileUpload({ onUpload, accept }) {
  const fileRef = useRef(); const cameraRef = useRef();
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <button type="button" style={uploadStyles.fileBtn} onClick={() => fileRef.current.click()}>Datei auswählen</button>
      <button type="button" style={uploadStyles.cameraBtn} onClick={() => cameraRef.current.click()}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgb(50,180,170)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
      </button>
      <input ref={fileRef} type="file" accept={accept || ".pdf,.jpg,.jpeg,.png"} style={{ display: 'none' }} onChange={e => onUpload(e)} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => onUpload(e)} />
    </div>
  );
}

const uploadStyles = {
  fileBtn: { padding: '6px 14px', backgroundColor: '#e8f0fe', color: 'rgb(50,180,170)', border: '1px solid rgb(50,180,170)', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' },
  cameraBtn: { padding: '4px 6px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', lineHeight: '1' },
};

function IconButton({ icon, tooltip, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span
        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: '2px' }}
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >{icon}</span>
      {hover && (
        <div style={{ position: 'absolute', bottom: '28px', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', padding: '5px 10px', backgroundColor: '#333', color: '#fff', borderRadius: '4px', fontSize: '11px', zIndex: 100, pointerEvents: 'none' }}>
          {tooltip}
          <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #333' }} />
        </div>
      )}
    </span>
  );
}

function AdminDashboard({ user, setUser }) {
  const [projects, setProjects] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', buyer_email: '', seller_email: '' });
  const [emailErrors, setEmailErrors] = useState({});
  const [selected, setSelected] = useState(null);
  const [allData, setAllData] = useState([]);
  const [docs, setDocs] = useState([]);
  const [sonstigeDocs, setSonstigeDocs] = useState([]);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [showArchive, setShowArchive] = useState(false);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [showPwChange, setShowPwChange] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');

  const [adminFormData, setAdminFormData] = useState({
    handaenderung: '', kaufpreis: '', reservationAnzahlung: '', reservationOption: '', reservationSonstige: '',
    zahlungPer: '', zahlungSonstige: '', hypothekUebernehmen: 'nein', hypothekBank: '', hypothekBankberater: '',
    hypothekTelefon: '', hypothekMail: '', hypothekHoehe: '', zahlungsversprechen: 'ja', bauland: 'nein',
    baulandDetail: '', zustand: '', notariatskosten: 'wird je zur Hälfte auf die Parteien aufgeteilt',
    zustellungEntwurf: 'Vorerst nur an Makler', bemerkungen: '',
  });

  const autoSaveTimer = useRef(null);
  const hasChanges = useRef(false);

  useEffect(() => { loadProjects(); }, []);

  const autoSave = useCallback(async () => {
    if (selected && hasChanges.current) {
      try { await axios.put(`/api/data/${selected.id}`, { data: adminFormData }); hasChanges.current = false; }
      catch (err) { console.log('Autosave:', err); setMessage('⚠️ Speichern fehlgeschlagen — bitte Verbindung prüfen'); }
    }
  }, [selected, adminFormData]);

  useEffect(() => { autoSaveTimer.current = setInterval(autoSave, 2000); return () => clearInterval(autoSaveTimer.current); }, [autoSave]);

  const loadProjects = async () => { const res = await axios.get('/api/projects'); setProjects(res.data); };
  const loadArchived = async () => {
    try { const res = await axios.get('/api/projects/archived/list'); setArchivedProjects(res.data); }
    catch (err) { console.log(err); }
  };

  const restoreProject = async (projectId) => {
    if (!window.confirm('Projekt wiederherstellen?')) return;
    try {
      await axios.post(`/api/projects/${projectId}/restore`);
      loadArchived(); loadProjects();
      setMessage('Projekt wiederhergestellt!'); setTimeout(() => setMessage(''), 6000);
    } catch (err) { setMessage('Fehler: ' + (err.response?.data?.error || err.message)); }
  };

  const permanentDelete = async (projectId) => {
    if (!window.confirm('⚠️ ACHTUNG: Projekt und alle Daten werden ENDGÜLTIG und UNWIDERRUFLICH gelöscht! Fortfahren?')) return;
    try {
      await axios.delete(`/api/projects/${projectId}/permanent`);
      loadArchived();
      setMessage('Projekt endgültig gelöscht'); setTimeout(() => setMessage(''), 6000);
    } catch (err) { setMessage('Fehler: ' + (err.response?.data?.error || err.message)); }
  };

  const createProject = async () => {
    const eErrs = {};
    if (!isValidEmail(newProject.buyer_email)) eErrs.buyer = true;
    if (!isValidEmail(newProject.seller_email)) eErrs.seller = true;
    setEmailErrors(eErrs);
    if (Object.keys(eErrs).length > 0) { setMessage('Bitte gültige E-Mail-Adressen eingeben'); return; }
    try {
      const res = await axios.post('/api/projects', newProject);
      setNewProject({ name: '', buyer_email: '', seller_email: '' });
      setShowNew(false); loadProjects();
      if (res.data.buyerToken && res.data.sellerToken) {
        const bLink = `${APP_URL}/set-password?token=${res.data.buyerToken}`;
        const sLink = `${APP_URL}/set-password?token=${res.data.sellerToken}`;
        let clip = `Käufer: ${bLink}\nVerkäufer: ${sLink}`;
        setMessage('Projekt erstellt! Links in Zwischenablage kopiert.');
        try { await navigator.clipboard.writeText(clip); } catch(e) { setMessage('Projekt erstellt!'); }
        setTimeout(() => setMessage(''), 30000);
      } else {
        setMessage('Projekt erstellt!'); setTimeout(() => setMessage(''), 6000);
      }
    } catch (err) { setMessage('Fehler: ' + (err.response?.data?.error || err.message)); }
  };

  const selectProject = async (project) => {
    if (selected && hasChanges.current) { await axios.put(`/api/data/${selected.id}`, { data: adminFormData }); hasChanges.current = false; }
    setSelected(project);
    const dataRes = await axios.get(`/api/data/${project.id}/all`);
    setAllData(dataRes.data);
    const adminD = dataRes.data.find(d => d.role === 'admin');
    if (adminD && adminD.data && Object.keys(adminD.data).length > 0) {
      setAdminFormData(prev => ({ ...prev, ...adminD.data }));
    } else {
      setAdminFormData({ handaenderung: '', kaufpreis: '', reservationAnzahlung: '', reservationOption: '', reservationSonstige: '', zahlungPer: '', zahlungSonstige: '', hypothekUebernehmen: 'nein', hypothekBank: '', hypothekBankberater: '', hypothekTelefon: '', hypothekMail: '', hypothekHoehe: '', zahlungsversprechen: 'ja', bauland: 'nein', baulandDetail: '', zustand: '', notariatskosten: 'wird je zur Hälfte auf die Parteien aufgeteilt', zustellungEntwurf: 'Vorerst nur an Makler', bemerkungen: '' });
    }
    const docsRes = await axios.get(`/api/documents/${project.id}`);
    setDocs(docsRes.data); setSonstigeDocs([]);
    // Status live aktualisieren
    loadProjects();
  };

  const closeProject = async () => {
    if (selected && hasChanges.current) { await axios.put(`/api/data/${selected.id}`, { data: adminFormData }); hasChanges.current = false; }
    setSelected(null); setAllData([]); setDocs([]);
  };

  const updateField = (field, value) => { setAdminFormData(prev => ({ ...prev, [field]: value })); hasChanges.current = true; };
  const updateCHFField = (field, value) => { setAdminFormData(prev => ({ ...prev, [field]: parseCHF(value) })); hasChanges.current = true; };

  const saveAdminData = async () => {
    await axios.put(`/api/data/${selected.id}`, { data: adminFormData }); hasChanges.current = false;
    setMessage('Daten gespeichert'); setTimeout(() => setMessage(''), 6000);
  };

  const uploadFile = async (e, kategorie) => {
    if (!e.target.files[0]) return;
    const formData = new FormData(); formData.append('file', e.target.files[0]); formData.append('kategorie', kategorie);
    await axios.post(`/api/documents/${selected.id}/upload`, formData);
    const docsRes = await axios.get(`/api/documents/${selected.id}`);
    setDocs(docsRes.data); setMessage('Hochgeladen'); setTimeout(() => setMessage(''), 6000); e.target.value = '';
  };

  const generatePdf = async () => {
    if (hasChanges.current) { await axios.put(`/api/data/${selected.id}`, { data: adminFormData }); hasChanges.current = false; }
    window.open(`${API_URL}/api/pdf/${selected.id}/generate`, '_blank');
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Projekt archivieren? Es wird 180 Tage aufbewahrt und kann wiederhergestellt werden.')) return;
    try {
      await axios.delete(`/api/projects/${projectId}`);
      setProjects(projects.filter(p => p.id !== projectId));
      if (selected && selected.id === projectId) { setSelected(null); setAllData([]); setDocs([]); }
      setMessage('Projekt archiviert (180 Tage wiederherstellbar)'); setTimeout(() => setMessage(''), 6000);
    } catch (err) { setMessage('Fehler: ' + (err.response?.data?.error || err.message)); }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('⚠️ Dokument wirklich unwiderruflich löschen?')) return;
    try { await axios.delete(`/api/documents/${docId}`); setDocs(docs.filter(d => d.id !== docId)); setMessage('Gelöscht'); setTimeout(() => setMessage(''), 6000); }
    catch (err) { setMessage('Fehler: ' + (err.response?.data?.error || err.message)); }
  };

  const reinvite = async (projectId, role) => {
    const label = role === 'buyer' ? 'Käufer' : role === 'seller' ? 'Verkäufer' : 'Notar';
    if (!window.confirm(`Neue Einladung für ${label} erstellen? Der bestehende Zugang wird ungültig.`)) return;
    try {
      const res = await axios.post(`/api/projects/${projectId}/reinvite`, { role });
      const link = `${APP_URL}/set-password?token=${res.data.token}`;
      setMessage(`✉️ Einladungslink für ${label}: ${link}`);
      try { await navigator.clipboard.writeText(link); setMessage(prev => prev + ' (kopiert!)'); } catch (e) {}
      setTimeout(() => setMessage(''), 15000);
    } catch (err) { setMessage('Fehler: ' + (err.response?.data?.error || err.message)); }
  };

  const resetPassword = async (projectId, role) => {
    const label = role === 'buyer' ? 'Käufer' : role === 'seller' ? 'Verkäufer' : 'Notar';
    if (!window.confirm(`Passwort für ${label} zurücksetzen? Ein neuer Link wird erstellt.`)) return;
    try {
      const res = await axios.post(`/api/projects/${projectId}/reset-password`, { role });
      const link = res.data.link;
      setMessage(`🔑 Passwort-Link für ${label}: ${link}`);
      try { await navigator.clipboard.writeText(link); setMessage(prev => prev + ' (kopiert!)'); } catch (e) {}
      setTimeout(() => setMessage(''), 15000);
    } catch (err) { setMessage('Fehler: ' + (err.response?.data?.error || err.message)); }
  };

  const openDocument = (doc) => { window.open(`${API_URL}/uploads/${doc.filename}`, '_blank'); };
  const addSonstigeDoc = () => { const sd = docs.filter(d => d.role === 'admin' && d.original_name?.startsWith('Sonstiges')); if (sd.length + sonstigeDocs.length + 1 >= 5) return; setSonstigeDocs([...sonstigeDocs, sonstigeDocs.length]); };

  const logout = async () => {
    if (selected && hasChanges.current) await axios.put(`/api/data/${selected.id}`, { data: adminFormData });
    await axios.post('/api/auth/logout'); setUser(null);
  };

  const inputStyle = (k) => ({ ...styles.input, borderColor: errors[k] ? '#dc3545' : '#ddd' });
  const selectStyle = (k) => ({ ...styles.select, borderColor: errors[k] ? '#dc3545' : '#ddd' });

  const statusLabel = (s, p) => {
    if (s === 'abgeschlossen') return { text: '✅ Abgeschlossen', color: '#28a745' };
    if (s === 'daten_komplett') return { text: '📋 Daten komplett', color: '#ff9800' };
    if (p && (p.buyer_submitted || p.seller_submitted)) return { text: '⏳ Teilweise', color: '#17a2b8' };
    return { text: '⏳ Offen', color: '#888' };
  };

  const renderPersonData = (role, data) => {
    if (!data || !data.personen) return <p>Noch keine Daten</p>;
    return (
      <div>
        {data.personen.map((p, i) => (
          <div key={i} style={{ marginBottom: '8px', fontSize: '13px' }}>
            <strong>Person {i + 1}:</strong> {p.vornamen?.join(' ')} {p.nachnamen?.join(' / ')}
            <br />Geb: {p.geburtsdatum} | {p.strasse}, {p.plz} {p.ort}
            {p.beruf && <><br />Beruf: {p.beruf}</>}
            <br />Mob/Tel: {p.telefon} | Mail: {p.email}
            <br />Nat: {p.nationalitaet === 'andere' ? p.nationalitaetAndere : p.nationalitaet} | Zivilstand: {p.zivilstand} {p.gueterrecht ? `(${p.gueterrecht})` : ''}
            {p.ahvNummer && <><br />AHV: {p.ahvNummer}</>}
          </div>
        ))}
        {data.finanzierung && (
          <div style={{ marginTop: '8px', fontSize: '13px' }}>
            <strong>Bank:</strong> {data.finanzierung.bank} | {data.finanzierung.ansprechperson}
            <br />Mob/Tel: {data.finanzierung.bankTelefon} | Mail: {data.finanzierung.bankMail}
            {data.finanzierung.hypothekarkontoIban && <><br />Hypo-IBAN: {data.finanzierung.hypothekarkontoIban}</>}
            {data.finanzierung.privatkontoIban && <><br />Privat-IBAN: {data.finanzierung.privatkontoIban}</>}
          </div>
        )}
        {data.eigentumsart && <div style={{ marginTop: '8px', fontSize: '13px' }}><strong>Eigentumsart:</strong> {data.eigentumsart}</div>}
        {data.sinaNachweis && <div style={{ marginTop: '8px', fontSize: '13px' }}><strong>SiNa:</strong> {data.sinaNachweis}</div>}
        {data.familienwohnung && <div style={{ marginTop: '8px', fontSize: '13px' }}><strong>Familienwohnung:</strong> {data.familienwohnung} {data.familienwohnungWer ? `(${data.familienwohnungWer})` : ''}</div>}
        {data.vermietet && <div style={{ marginTop: '8px', fontSize: '13px' }}><strong>Vermietet:</strong> {data.vermietet}</div>}
      </div>
    );
  };

  const renderRoleDocs = (role) => {
    const roleDocs = docs.filter(d => d.role === role);
    if (roleDocs.length === 0) return <p style={{ fontSize: '12px', color: '#888' }}>Keine Dokumente</p>;
    return roleDocs.map(d => (
      <div key={d.id} style={{ fontSize: '12px', padding: '2px 0', display: 'flex', alignItems: 'center' }}>
        <span style={{ color: '#dc3545', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', marginRight: '6px' }}
          onClick={() => { if (window.confirm('⚠️ ACHTUNG: Dokument "' + d.original_name + '" wirklich unwiderruflich löschen?')) deleteDocument(d.id); }}>✕</span>
        <span style={styles.docLink} onClick={() => openDocument(d)}>📄 {d.original_name}</span>
      </div>
    ));
  };

  const filteredProjects = projects.filter(p => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (p.project_uid || '').toLowerCase().includes(s) || (p.name || '').toLowerCase().includes(s) ||
      (p.buyer_email || '').toLowerCase().includes(s) || (p.seller_email || '').toLowerCase().includes(s);
  }).sort((a, b) => {
    if (!sortBy) return 0;
    const so = { 'pending': 0, 'daten_komplett': 1, 'abgeschlossen': 2 };
    switch (sortBy) {
      case 'uid': return (a.project_uid || '').localeCompare(b.project_uid || '');
      case 'uid_desc': return (b.project_uid || '').localeCompare(a.project_uid || '');
      case 'name': return (a.name || '').localeCompare(b.name || '');
      case 'name_desc': return (b.name || '').localeCompare(a.name || '');
      case 'status': return (so[a.status] || 0) - (so[b.status] || 0);
      case 'status_desc': return (so[b.status] || 0) - (so[a.status] || 0);
      default: return 0;
    }
  });

  const sonstigeAdminDocs = docs.filter(d => d.role === 'admin' && !['AGV', 'Grundbuchauszug'].some(k => d.original_name?.startsWith(k)));

  return (
    <div style={styles.container}>
      <div style={styles.pageHeader}>
        <h2>Admin Dashboard</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {user.is_superadmin && <button style={styles.navBtn} onClick={() => window.location.href = '/admin-users'}>👥 Admins</button>}
          {user.is_superadmin && <button style={styles.navBtn} onClick={() => window.location.href = '/all-projects'}>📂 Alle Projekte</button>}
          <button style={styles.navBtn} onClick={() => { setShowArchive(!showArchive); if (!showArchive) loadArchived(); }}>🗄️ Projekt Archiv</button>
          <span style={styles.email}>{user.email}</span>
          <IconButton
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>}
            tooltip="Passwort ändern"
            onClick={() => setShowPwChange(true)} />
          <button style={styles.logoutBtn} onClick={logout}>Logout</button>
          <img src="/logo.jpg" alt="EliteHomes" style={styles.logoSmall} />
        </div>
      </div>

      {message && <div style={styles.message}>{message}</div>}

      {showPwChange && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3>Passwort ändern</h3>
            <button style={styles.closeBtn} onClick={() => { setShowPwChange(false); setPwCurrent(''); setPwNew(''); setPwConfirm(''); }}>✕</button>
          </div>
          <input style={styles.input} type="password" placeholder="Aktuelles Passwort" value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} />
          <input style={styles.input} type="password" placeholder="Neues Passwort" value={pwNew} onChange={e => setPwNew(e.target.value)} />
          <p style={{ fontSize: '11px', color: '#999', marginTop: '-4px', marginBottom: '8px' }}>Mind. 8 Zeichen, 1 Grossbuchstabe, 1 Zahl</p>
          <input style={styles.input} type="password" placeholder="Neues Passwort bestätigen" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} />
          <button style={styles.button} onClick={async () => {
            if (pwNew !== pwConfirm) { setMessage('Passwörter stimmen nicht überein'); return; }
            try {
              await axios.post('/api/auth/change-password', { currentPassword: pwCurrent, newPassword: pwNew });
              setMessage('Passwort erfolgreich geändert!'); setShowPwChange(false); setPwCurrent(''); setPwNew(''); setPwConfirm('');
              setTimeout(() => setMessage(''), 6000);
            } catch (err) { setMessage('Fehler: ' + (err.response?.data?.error || err.message)); }
          }}>Passwort ändern</button>
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3>Meine Projekte</h3>
          <button style={styles.button} onClick={() => setShowNew(!showNew)}>+ Neues Projekt</button>
        </div>

        {showNew && (
          <div style={styles.newForm}>
            <input style={styles.input} placeholder="Projektname" value={newProject.name} onChange={e => setNewProject({...newProject, name: e.target.value})} />
            <input style={{...styles.input, borderColor: emailErrors.buyer ? '#dc3545' : '#ddd'}} type="email" placeholder="E-Mail Käufer" value={newProject.buyer_email} onChange={e => setNewProject({...newProject, buyer_email: e.target.value})} />
            {emailErrors.buyer && <span style={styles.errorText}>Gültige E-Mail erforderlich</span>}
            <input style={{...styles.input, borderColor: emailErrors.seller ? '#dc3545' : '#ddd'}} type="email" placeholder="E-Mail Verkäufer" value={newProject.seller_email} onChange={e => setNewProject({...newProject, seller_email: e.target.value})} />
            
            {emailErrors.seller && <span style={styles.errorText}>Gültige E-Mail erforderlich</span>}
            <button style={styles.button} onClick={createProject}>Erstellen & Einladen</button>
          </div>
        )}

        <input style={{...styles.input, marginTop: '12px'}} placeholder="🔍 Suchen nach ID, Name, E-Mail..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />

        <table style={styles.table}>
          <thead><tr>
            <th style={{...styles.th, cursor: 'pointer'}} onClick={() => setSortBy(sortBy === 'uid' ? 'uid_desc' : 'uid')}>ID {sortBy.startsWith('uid') ? (sortBy === 'uid' ? '↑' : '↓') : ''}</th>
            <th style={{...styles.th, cursor: 'pointer'}} onClick={() => setSortBy(sortBy === 'name' ? 'name_desc' : 'name')}>Name {sortBy.startsWith('name') ? (sortBy === 'name' ? '↑' : '↓') : ''}</th>
            <th style={styles.th}>Käufer</th>
            <th style={styles.th}>Verkäufer</th>
            <th style={{...styles.th, cursor: 'pointer'}} onClick={() => setSortBy(sortBy === 'status' ? 'status_desc' : 'status')}>Status {sortBy.startsWith('status') ? (sortBy === 'status' ? '↑' : '↓') : ''}</th>
            <th style={styles.th}>Aktion</th>
          </tr></thead>
          <tbody>
            {filteredProjects.map(p => {
              const st = statusLabel(p.status, p);
              return (
                <tr key={p.id} style={selected && selected.id === p.id ? { backgroundColor: '#e8f0fe' } : {}}>
                  <td style={styles.td}>{p.project_uid}</td>
                  <td style={styles.td}>{p.name}</td>
                  <td style={styles.td}>{p.buyer_email || '-'} {p.buyer_submitted ? '✅' : p.buyer_first_login ? <span style={{ position: 'relative', display: 'inline-block', width: '18px', height: '18px' }}>⏳<svg style={{ position: 'absolute', bottom: '1px', right: '-4px' }} width="10" height="10" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="11" fill="#fff"/><path d="M6 13l4 4 8-9" stroke="#28a745" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg></span> : p.buyer_inactive ? <span style={{ position: 'relative', display: 'inline-block', width: '18px', height: '18px' }}>⏳<svg style={{ position: 'absolute', bottom: '1px', right: '-4px' }} width="10" height="10" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="11" fill="#dc3545"/><path d="M12 7v6M12 16v1" stroke="#fff" strokeWidth="3" strokeLinecap="round"/></svg></span> : '⏳'}</td>
                  <td style={styles.td}>{p.seller_email || '-'} {p.seller_submitted ? '✅' : p.seller_first_login ? <span style={{ position: 'relative', display: 'inline-block', width: '18px', height: '18px' }}>⏳<svg style={{ position: 'absolute', bottom: '1px', right: '-4px' }} width="10" height="10" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="11" fill="#fff"/><path d="M6 13l4 4 8-9" stroke="#28a745" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg></span> : p.seller_inactive ? <span style={{ position: 'relative', display: 'inline-block', width: '18px', height: '18px' }}>⏳<svg style={{ position: 'absolute', bottom: '1px', right: '-4px' }} width="10" height="10" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="11" fill="#dc3545"/><path d="M12 7v6M12 16v1" stroke="#fff" strokeWidth="3" strokeLinecap="round"/></svg></span> : '⏳'}</td>
                  <td style={{...styles.td, color: st.color, fontWeight: '600', fontSize: '12px'}}>{st.text}</td>
                  <td style={styles.td}>
                    <button style={styles.smallBtn} onClick={() => selectProject(p)}>Öffnen</button>
                    <button style={styles.deleteBtn} onClick={() => deleteProject(p.id)}>Löschen</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showArchive && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3>🗄️ Archivierte Projekte</h3>
            <button style={styles.closeBtn} onClick={() => setShowArchive(false)}>✕</button>
          </div>
          {archivedProjects.length === 0 ? (
            <p style={{ color: '#888', fontSize: '14px', marginTop: '12px' }}>Keine archivierten Projekte</p>
          ) : (
            <table style={styles.table}>
              <thead><tr><th style={styles.th}>ID</th><th style={styles.th}>Name</th><th style={styles.th}>Archiviert am</th><th style={styles.th}>Löschung am</th><th style={styles.th}>Aktion</th></tr></thead>
              <tbody>
                {archivedProjects.map(p => {
                  const archDate = new Date(p.archived_at);
                  const deleteDate = new Date(archDate.getTime() + 180 * 24 * 60 * 60 * 1000);
                  const archStr = archDate.getDate().toString().padStart(2, '0') + '.' + (archDate.getMonth() + 1).toString().padStart(2, '0') + '.' + archDate.getFullYear();
                  const delStr = deleteDate.getDate().toString().padStart(2, '0') + '.' + (deleteDate.getMonth() + 1).toString().padStart(2, '0') + '.' + deleteDate.getFullYear();
                  return (
                    <tr key={p.id}>
                      <td style={styles.td}>{p.project_uid}</td>
                      <td style={styles.td}>{p.name}</td>
                      <td style={styles.td}>{archStr}</td>
                      <td style={{...styles.td, color: '#dc3545', fontSize: '12px'}}>{delStr}</td>
                      <td style={styles.td}>
                        <button style={styles.smallBtn} onClick={() => restoreProject(p.id)}>↩ Wiederherstellen</button>
                        {user.is_superadmin && <button style={{...styles.deleteBtn, marginLeft: '6px'}} onClick={() => permanentDelete(p.id)}>Endgültig löschen</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {selected && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3>Projekt: {selected.project_uid} — {selected.name}</h3>
            <button style={styles.closeBtn} onClick={closeProject}>✕ Schliessen</button>
          </div>

          <div style={styles.dataOverview}>
            {allData.filter(d => d.role !== 'admin').map(d => (
              <div key={d.id} style={styles.dataBlock}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4>{d.role === 'buyer' ? 'Käufer' : d.role === 'seller' ? 'Verkäufer' : 'Notar'} {d.submitted ? '✅' : '⏳'}</h4>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {!d.submitted && (
                      <IconButton tooltip="Neue Einladung senden" onClick={() => reinvite(selected.id, d.role)}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>} />
                    )}
                    <IconButton tooltip="Passwort zurücksetzen" onClick={() => resetPassword(selected.id, d.role)}
                      icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>} />
                  </div>
                </div>
                {renderPersonData(d.role, d.data)}
                <div style={{ marginTop: '8px', borderTop: '1px solid #ddd', paddingTop: '8px' }}>
                  <strong style={{ fontSize: '12px' }}>Dokumente:</strong>
                  {renderRoleDocs(d.role)}
                </div>
              </div>
            ))}
          </div>

          <div style={styles.sectionInner}>
            <h3>Notariat Angaben</h3>
            <p style={styles.autoSaveHint}>Daten werden automatisch gespeichert</p>

            <label style={styles.label}>Handänderung *</label>
            <input style={{...inputStyle('handaenderung'), width: '180px'}} type="date" value={adminFormData.handaenderung} onChange={e => updateField('handaenderung', e.target.value)} />

            <label style={styles.label}>Kaufpreis (CHF) *</label>
            <input style={inputStyle('kaufpreis')} placeholder="CHF" value={formatCHF(adminFormData.kaufpreis)} onChange={e => updateCHFField('kaufpreis', e.target.value)} />

            <label style={styles.label}>Reservation / Anzahlung (CHF) *</label>
            <input style={inputStyle('reservationAnzahlung')} placeholder="CHF" value={formatCHF(adminFormData.reservationAnzahlung)} onChange={e => updateCHFField('reservationAnzahlung', e.target.value)} />
            <select style={selectStyle('reservationOption')} value={adminFormData.reservationOption} onChange={e => updateField('reservationOption', e.target.value)}>
              <option value="">— Bitte wählen — *</option>{RESERVATION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            {adminFormData.reservationOption === 'sonstige' && <textarea style={styles.textarea} placeholder="Sonstige (max. 200)" maxLength={200} value={adminFormData.reservationSonstige} onChange={e => updateField('reservationSonstige', e.target.value)} />}

            <label style={styles.label}>Zahlung per *</label>
            <select style={selectStyle('zahlungPer')} value={adminFormData.zahlungPer} onChange={e => updateField('zahlungPer', e.target.value)}>
              <option value="">— Bitte wählen —</option>{ZAHLUNG_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            {adminFormData.zahlungPer === 'sonstige Vereinbarung' && <textarea style={styles.textarea} placeholder="Vereinbarung (max. 200)" maxLength={200} value={adminFormData.zahlungSonstige} onChange={e => updateField('zahlungSonstige', e.target.value)} />}

            <label style={styles.label}>Hypothek übernehmen? *</label>
            <select style={selectStyle('hypothekUebernehmen')} value={adminFormData.hypothekUebernehmen} onChange={e => updateField('hypothekUebernehmen', e.target.value)}>
              {HYPOTHEK_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            {adminFormData.hypothekUebernehmen === 'ja' && (
              <div style={{ marginTop: '8px' }}>
                <label style={styles.label}>Bank</label><input style={styles.input} placeholder="Bank" value={adminFormData.hypothekBank} onChange={e => updateField('hypothekBank', e.target.value)} />
                <label style={styles.label}>Bankberater*in *</label><input style={inputStyle('hypothekBankberater')} placeholder="Bankberater*in" value={adminFormData.hypothekBankberater} onChange={e => updateField('hypothekBankberater', e.target.value)} />
                <label style={styles.label}>Mobile / Telefon *</label><input style={inputStyle('hypothekTelefon')} type="tel" placeholder="z.B. 079 801 55 55" value={adminFormData.hypothekTelefon} onChange={e => updateField('hypothekTelefon', e.target.value)} />
                <label style={styles.label}>Mail *</label><input style={inputStyle('hypothekMail')} type="email" placeholder="E-Mail" value={adminFormData.hypothekMail} onChange={e => updateField('hypothekMail', e.target.value)} />
                <label style={styles.label}>Höhe (CHF) *</label><input style={inputStyle('hypothekHoehe')} placeholder="CHF" value={formatCHF(adminFormData.hypothekHoehe)} onChange={e => updateCHFField('hypothekHoehe', e.target.value)} />
              </div>
            )}

            <label style={styles.label}>Zahlungsversprechen? *</label>
            <select style={selectStyle('zahlungsversprechen')} value={adminFormData.zahlungsversprechen} onChange={e => updateField('zahlungsversprechen', e.target.value)}>
              {ZAHLUNGSVERSPRECHEN_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>

            <label style={styles.label}>Bauland *</label>
            <select style={selectStyle('bauland')} value={adminFormData.bauland} onChange={e => updateField('bauland', e.target.value)}>
              {BAULAND_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            {adminFormData.bauland === 'ja' && <select style={selectStyle('baulandDetail')} value={adminFormData.baulandDetail} onChange={e => updateField('baulandDetail', e.target.value)}><option value="">— Bitte wählen —</option>{BAULAND_DETAIL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}</select>}

            <label style={styles.label}>Zustand Übergabe</label>
            <select style={styles.select} value={adminFormData.zustand} onChange={e => updateField('zustand', e.target.value)}>
              <option value="">— Bitte wählen —</option><option value="besenrein">besenrein</option><option value="gereinigt">gereinigt</option><option value="wie gesehen / aktueller Zustand">wie gesehen / aktueller Zustand</option>
            </select>

            <label style={styles.label}>Notariats-/Grundbuchkosten *</label>
            <select style={selectStyle('notariatskosten')} value={adminFormData.notariatskosten} onChange={e => updateField('notariatskosten', e.target.value)}>
              {KOSTEN_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>

            <label style={styles.label}>Zustellung Entwurf an *</label>
            <select style={selectStyle('zustellungEntwurf')} value={adminFormData.zustellungEntwurf} onChange={e => updateField('zustellungEntwurf', e.target.value)}>
              {ZUSTELLUNG_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>

            <label style={styles.label}>Bemerkungen</label>
            <textarea style={{...styles.textarea, height: '80px'}} placeholder="Bemerkungen" value={adminFormData.bemerkungen} onChange={e => updateField('bemerkungen', e.target.value)} />

            <div style={styles.btnRow}><button style={styles.saveBtn} onClick={saveAdminData}>Manuell speichern</button></div>
          </div>

          <div style={styles.sectionInner}>
            <h3>Dokumente hochladen</h3>
            <label style={styles.label}>AGV Police</label>
            {(() => { const doc = docs.find(d => d.role === 'admin' && d.original_name?.startsWith('AGV')); return doc ? (<div style={styles.docInline}><span style={styles.docLink} onClick={() => openDocument(doc)}>📄 {doc.original_name}</span><button style={styles.docDeleteBtn} onClick={() => deleteDocument(doc.id)}>✕</button></div>) : <FileUpload onUpload={e => uploadFile(e, 'AGV Police')} />; })()}
            <label style={styles.label}>Grundbuchauszug</label>
            {(() => { const doc = docs.find(d => d.role === 'admin' && d.original_name?.startsWith('Grundbuchauszug')); return doc ? (<div style={styles.docInline}><span style={styles.docLink} onClick={() => openDocument(doc)}>📄 {doc.original_name}</span><button style={styles.docDeleteBtn} onClick={() => deleteDocument(doc.id)}>✕</button></div>) : <FileUpload onUpload={e => uploadFile(e, 'Grundbuchauszug')} />; })()}
            <label style={styles.label}>Sonstige (max. 5)</label>
            {sonstigeAdminDocs.length < 5 && <FileUpload onUpload={e => uploadFile(e, 'Sonstiges')} />}
            {sonstigeDocs.map((_, i) => (sonstigeAdminDocs.length + i + 1 < 5 && <div key={i} style={{ marginTop: '6px' }}><FileUpload onUpload={e => uploadFile(e, 'Sonstiges')} /></div>))}
            {sonstigeAdminDocs.length < 5 && sonstigeDocs.length < 4 && <button style={styles.addBtn} onClick={addSonstigeDoc}>+ Weiteres Dokument</button>}
            {sonstigeAdminDocs.length > 0 && <h4 style={{ marginTop: '16px' }}>Sonstige Dokumente</h4>}
            {sonstigeAdminDocs.map(d => (<div key={d.id} style={styles.docItem}><span style={styles.docLink} onClick={() => openDocument(d)}>📄 {d.original_name}</span><button style={styles.docDeleteBtn} onClick={() => deleteDocument(d.id)}>✕</button></div>))}
          </div>

<div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
            <h4 style={{ marginBottom: '8px' }}>Notar</h4>
            {selected.notar_email ? (
              <div>
                <p style={{ fontSize: '13px', color: '#28a745' }}>✅ Eingeladen: {selected.notar_email}</p>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px', alignItems: 'center' }}>
                  <IconButton tooltip="Neue Einladung senden" onClick={() => reinvite(selected.id, 'notar')}
                    icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>} />
                  <IconButton tooltip="Passwort zurücksetzen" onClick={() => resetPassword(selected.id, 'notar')}
                    icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>} />
                </div>
              </div>
            ) : (
              <div>
                {(() => {
                  const buyerD = allData.find(d => d.role === 'buyer');
                  const sellerD = allData.find(d => d.role === 'seller');
                  const ad = adminFormData;
                  const adminMissing = [];
                  if (!ad.handaenderung) adminMissing.push('Handänderung');
                  if (!ad.kaufpreis) adminMissing.push('Kaufpreis');
                  if (!ad.reservationAnzahlung) adminMissing.push('Reservation/Anzahlung');
                  if (!ad.reservationOption) adminMissing.push('Reservation-Option');
                  if (ad.reservationOption === 'sonstige' && !ad.reservationSonstige) adminMissing.push('Reservation-Sonstige');
                  if (!ad.zahlungPer) adminMissing.push('Zahlung per');
                  if (ad.zahlungPer === 'sonstige Vereinbarung' && !ad.zahlungSonstige) adminMissing.push('Zahlung-Sonstige');
                  if (ad.hypothekUebernehmen === 'ja') {
                    if (!ad.hypothekBankberater) adminMissing.push('Hypothek-Bankberater');
                    if (!ad.hypothekTelefon) adminMissing.push('Hypothek-Telefon');
                    if (!ad.hypothekMail) adminMissing.push('Hypothek-Mail');
                    if (!ad.hypothekHoehe) adminMissing.push('Hypothek-Höhe');
                  }
                  if (ad.bauland === 'ja' && !ad.baulandDetail) adminMissing.push('Bauland-Detail');
                  const allComplete = buyerD?.submitted && sellerD?.submitted && adminMissing.length === 0;
                  if (!allComplete) {
                    const parts = [];
                    if (!buyerD?.submitted) parts.push('Käufer');
                    if (!sellerD?.submitted) parts.push('Verkäufer');
                    if (adminMissing.length > 0) parts.push('Admin: ' + adminMissing.join(', '));
                    return <p style={{ fontSize: '12px', color: '#ff9800' }}>⚠️ Notar kann erst eingeladen werden. Fehlt: {parts.join(' | ')}</p>;
                  }
                  return (
                    <div>
                      <p style={{ fontSize: '12px', color: '#28a745', marginBottom: '8px' }}>✅ Alle Daten komplett — Notar kann eingeladen werden.</p>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input style={{...styles.input, width: '250px', marginBottom: 0}} type="email" placeholder="E-Mail des Notars" id="notar-email-input" />
                        <button style={styles.button} onClick={async () => {
                          const email = document.getElementById('notar-email-input').value;
                          if (!email || !email.includes('@')) { setMessage('Gültige E-Mail erforderlich'); return; }
                          try {
                            const res = await axios.post(`/api/projects/${selected.id}/invite-notar`, { email });
                            const link = `${APP_URL}/set-password?token=${res.data.token}`;
                            setMessage(`✉️ Notar-Link: ${link}`);
                            try { await navigator.clipboard.writeText(link); setMessage(prev => prev + ' (kopiert!)'); } catch(e) {}
                            setTimeout(() => setMessage(''), 15000);
                            loadProjects();
                            selectProject(selected);
                          } catch (err) { setMessage('Fehler: ' + (err.response?.data?.error || err.message)); }
                        }}>Notar einladen</button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          <div style={{ marginTop: '20px' }}>
            {(() => {
              const buyerD = allData.find(d => d.role === 'buyer');
              const sellerD = allData.find(d => d.role === 'seller');
              const missing = [];
              if (!buyerD?.submitted) missing.push('Käufer');
              if (!sellerD?.submitted) missing.push('Verkäufer');
              const adminMissing = [];
              if (!adminFormData.handaenderung) adminMissing.push('Handänderung');
              if (!adminFormData.kaufpreis) adminMissing.push('Kaufpreis');
              if (!adminFormData.reservationOption) adminMissing.push('Reservation');
              if (!adminFormData.zahlungPer) adminMissing.push('Zahlung');
              return (
                <>
                  {missing.length > 0 && <p style={{ fontSize: '12px', color: '#ff9800', marginBottom: '4px' }}>⚠️ Noch ausstehend: {missing.join(', ')}</p>}
                  {adminMissing.length > 0 && <p style={{ fontSize: '12px', color: '#dc3545', marginBottom: '8px' }}>⚠️ Admin-Felder fehlen: {adminMissing.join(', ')}</p>}
                  <button style={styles.generateBtn} onClick={generatePdf}>📋 PDF Dossier erstellen</button>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { maxWidth: '1000px', margin: '0 auto', padding: '20px', minHeight: '100vh' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  logoSmall: { width: '120px', height: 'auto' },
  email: { color: '#666', fontSize: '14px' },
  logoutBtn: { padding: '8px 16px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  navBtn: { padding: '8px 16px', backgroundColor: 'rgb(50,180,170)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  closeBtn: { padding: '8px 16px', backgroundColor: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  section: { backgroundColor: 'rgba(255,255,255,0.95)', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginBottom: '20px' },
  sectionInner: { marginTop: '24px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '6px' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  button: { padding: '10px 20px', backgroundColor: 'rgb(50,180,170)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '8px' },
  smallBtn: { padding: '6px 12px', backgroundColor: 'rgb(50,180,170)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  deleteBtn: { padding: '6px 12px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', marginLeft: '6px' },
  generateBtn: { padding: '12px 24px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '16px' },
  saveBtn: { padding: '12px 24px', backgroundColor: 'rgb(50,180,170)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '16px' },
  input: { width: '100%', padding: '10px', marginBottom: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#fff', marginBottom: '8px' },
  textarea: { width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box', height: '60px', marginBottom: '8px' },
  label: { display: 'block', marginTop: '12px', marginBottom: '4px', fontWeight: '600', fontSize: '14px', color: '#333' },
  addBtn: { padding: '6px 12px', backgroundColor: '#e8f0fe', color: 'rgb(50,180,170)', border: '1px solid rgb(50,180,170)', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', marginTop: '6px' },
  newForm: { marginTop: '12px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '4px' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: '12px' },
  th: { textAlign: 'left', padding: '10px', borderBottom: '2px solid #ddd', backgroundColor: '#f8f9fa', fontSize: '13px' },
  td: { padding: '10px', borderBottom: '1px solid #eee', fontSize: '13px' },
  message: { padding: '10px', backgroundColor: '#d4edda', borderRadius: '4px', marginBottom: '16px', color: '#155724' },
  dataOverview: { display: 'flex', gap: '12px', marginBottom: '20px' },
  dataBlock: { flex: 1, padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '13px' },
  btnRow: { display: 'flex', gap: '12px', marginTop: '24px' },
  docItem: { padding: '8px 0', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  docInline: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', backgroundColor: '#e8f5e9', borderRadius: '4px', marginBottom: '4px' },
  docLink: { cursor: 'pointer', color: 'rgb(50,180,170)', textDecoration: 'underline', fontSize: '14px' },
  docDeleteBtn: { padding: '2px 8px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  errorText: { color: '#dc3545', fontSize: '12px', display: 'block', marginBottom: '8px' },
  autoSaveHint: { fontSize: '12px', color: '#888', fontStyle: 'italic', marginBottom: '8px' },
  
};

export default AdminDashboard;